'use strict';

define('vb/action/eventAction',[
  'vb/private/constants',
  'vb/private/log',
  'vb/action/action',
  'vb/private/stateManagement/router',
  'vb/private/action/assignmentHelper',
  'vb/private/events/eventPayloadMetadata',
  'vb/private/utils',
],
(Constants, Log, Action, Router, AssignmentHelper, EventPayloadMetadata, Utils) => {
  /** @typedef {import('vb/private/events/eventModel').default} EventModel */

  const logger = Log.getLogger('/vb/action/eventAction');

  const CAMELCASE_REG = /^[a-z][A-Za-z]*$/;

  /**
   * not allowed to create event using existing VB event name
   */
  const INVALID_EVENTS = [
    Constants.ENTER_EVENT,
    Constants.EXIT_EVENT,
    Constants.BEFORE_ENTER_EVENT,
    Constants.BEFORE_EXIT_EVENT,
    Constants.NOTIFICATION_EVENT,
    Constants.AFTER_NAVIGATE_EVENT,
    Constants.INSTALL_PROMPT_EVENT,
    Constants.DATAPROVIDER_NOTIFICATION_EVENT,
    Constants.RESOURCE_CHANGED_EVENT,
    Constants.NEW_CONTENT_AVAILABLE,
  ];

  /**
   * Abstract base class representing an event action.
   *
   * @extends Action
   * @category Action
   */
  class EventAction extends Action {
    /**
     * Performs the event action.
     *
     * This method is intended to be overridden by subclasses. It throws an error if called directly on the
     * EventAction class.
     *
     * @abstract
     * @throws {Error} If called directly on the EventAction class.
     * @param {Object} parameters - Parameters for the event action.
     */
    // eslint-disable-next-line class-methods-use-this, no-unused-vars
    perform(parameters) {
      throw new Error('Cannot instantiate EventAction.');
    }

    /**
     * Sets the context for the event action.
     *
     * @param {Object} context - The context object to set.
     * @description If the context object contains a container with an invokeEventWithBubbling method,
     *              sets the eventContainer property to that container.
     */
    setContext(context) {
      this.context = context;
      if (context && context.container && context.container.invokeEventWithBubbling
        && typeof context.container.invokeEventWithBubbling === 'function') {
        this.eventContainer = context.container;
      }
    }

    /**
     * Checks if the specified target is valid.
     *
     * A target is considered valid if it is either undefined, null, or matches one of the predefined constants:
     * {@link Constants.EventTargets#LEAF} or {@link Constants.EventTargets#CURRENT}.
     *
     * @param {string} target - The target to validate.
     * @returns {boolean} True if the target is valid, false otherwise.
     */
    static isValidTarget(target) {
      return target === undefined || target === null
        || target === Constants.EventTargets.LEAF || target === Constants.EventTargets.CURRENT;
    }

    /**
     * Fires an event on the specified container.
     *
     * @param {Object} eventContainer - The container that's firing the given event.
     * @param {String} name - The name of the event to fire.
     * @param {Object} payload - The payload associated with the event.
     * @param {Object} [options] - Optional parameters for the action.
     * @param {String} [options.target=Constants.EventTargets.CURRENT] - The target container for the event.
     * @returns {(Promise|null)} A promise that resolves when the event is fired, or null if the event was not fired.
     */
    static fireEvent(eventContainer, name, payload, options) {
      const container = EventAction.getEventTargetContainer(eventContainer, name, options);

      return EventAction.fireEventOnTarget(container, name, payload);
    }

    /**
     * Fires an event on a resolved target container.
     *
     * @param {Object} container - The resolved target container.
     * @param {String} name - The name of the event to fire.
     * @param {Object} payload - The payload associated with the event.
     * @returns {(Promise|null)} A promise that resolves when the event is fired, or null if the event was not fired.
     */
    static fireEventOnTarget(container, name, payload) {
      if (container) {
        // returns Promise
        return container.invokeEventWithBubbling(name, payload);
      }

      return null;
    }

    /**
     * Resolves the container that will receive a fired event.
     *
     * @param {Object} eventContainer - The container that's firing the given event.
     * @param {String} name - The name of the event to fire.
     * @param {Object} [options] - Optional parameters for the action.
     * @param {String} [options.target=Constants.EventTargets.CURRENT] - The target container for the event.
     * @returns {Object|null} container that should receive the event
     */
    static getEventTargetContainer(eventContainer, name, options) {
      if (options && options.target === Constants.EventTargets.LEAF) {
        // Allow container to determine what its leaf container is. Cannot assume that leaf always starts from a page
        // because fragments are lesser scope than page and can fire events that are only listenable by fragment
        const container = (eventContainer && eventContainer.getLeafContainer()) || Router.getCurrentPage();

        if (!container) {
          logger.warn(`Could not get the current page for event: ${name}`);
        }

        return container;
      }

      return eventContainer;
    }

    /**
     * Performs a custom event propagation behavior based on the setting for 'propagationBehavior' property on
     * eventModel.
     *
     * @param {Object} eventContainer - The container that's firing the given event.
     * @param {Object} internalContext - The internal context object.
     * @param {String} name - The event name; must be declared in "events" and accessible to this container.
     * @param {Object} payload - The event payload.
     * @param {EventModel} eventModel - The event model for 'propagationBehavior:self/container'.
     * @param {Object} [options] - Optional behavior for the propagation dispatch.
     * @param {boolean} [options.preservePayloadRowContextMetadata=false] - Preserve row metadata after payload
     * coercion.
     *
     * @throws {Error} If the event model is not declared or if the event name is not valid.
     *
     * @static
     */
    static performPropagationBehavior(eventContainer, internalContext, name, payload, eventModel, options) {
      // 'container' propagation event behavior assumes the consumer of the event is a cca,
      // so event is dispatched via special dispatchEvent
      if (eventContainer
        && eventContainer.allowsEventPropagation(Constants.EventPropagationBehaviors.CONTAINER, eventModel)) {
        // must be declared but need not be part of an interface; interface events are only needed for extensions
        if (!eventModel || !eventModel.isDeclared) {
          throw new Error(`Unable to find event declaration for: ${name}`);
        }

        const coercedPayload = eventModel.payloadType
          ? AssignmentHelper.coerceType(payload, eventModel.payloadType) : payload;
        if (options && options.preservePayloadRowContextMetadata) {
          EventPayloadMetadata.preserveEventPayloadRowContextMetadata(payload, coercedPayload);
        }

        // at this point the event model has been found and validated,
        // and if the event name is not using camelCase or lowercase a warning has been logged
        // so it's safe to use a short event name, for cases when an event is referenced like
        // fragment:name, layout:name, etc.
        EventAction.raiseEvent(eventContainer, internalContext, eventModel.name, coercedPayload, false);
      }
    }

    /**
     * Raises an event on the specified container using the JET-provided abstraction.
     *
     * @param {Object} eventContainer - The container that's firing the given event.
     * @param {Object} internalContext - The internal context object.
     * @param {String} name - The name of the event to raise.
     * @param {Object} payload - The payload associated with the event.
     * @param {Boolean} [isDynamicComponent=true] - Indicates whether it's a dynamic component event or not.
     * @throws {Error} If no component event dispatcher is provided.
     * @returns {void}
     */
    static raiseEvent(eventContainer, internalContext, name, payload, isDynamicComponent = true) {
      const dispatch = EventAction.getDispatcher(eventContainer, internalContext);

      if (!dispatch) {
        throw new Error(`Unable to fire event '${name}', as no component event dispatcher provided.`);
      }

      try {
        // the JET-provided function will raise the event on the dynamic UI component
        const event = new Event(name, { bubbles: false });
        // @ts-ignore
        event.detail = payload;
        dispatch(event);
        const cont = isDynamicComponent ? 'Dynamic' : '';
        logger.info('Dispatched', cont, 'Container event:', name, 'result');
      } catch (e) {
        logger.error(`Exception firing DOM event: ${name}`, e);
        throw e;
      }
    }

    /**
     * Returns the JET-provided function.
     * @param eventContainer {Object} container that's firing the given event
     * @param internalContext {Object} internal context object
     * @returns {*|{vbBridge}|{vbBridge: {create: function(): {addVariableValueChangedListener: function(): boolean,
     * removeVariableValueChangedListener: function(): boolean}}}|boolean}
     */
    static getDispatcher(eventContainer, internalContext) {
      // get the JET interface / abstraction for raising the event on the dynamic container
      const $bindingContext = internalContext && internalContext[Constants.ContextName.BINDING_CONTEXT];

      // @todo: the name & shape of this property will be defined by JET
      let dispatcher = Utils.getDispatcher($bindingContext);

      // when this action is called from a nested chain (callChainAction containing a fireCustomEventAction) the
      // internalContext may not be transmitted down. See actionChain.EXCLUDED_CHAIN_SCOPE_VARS and
      // setInternalContext. In such cases attempt to retrieve the dispatcher from the event container, such as a
      // fragment
      if (!dispatcher && eventContainer) {
        dispatcher = typeof eventContainer.getEventDispatcher === 'function' && eventContainer.getEventDispatcher();
      }

      return dispatcher;
    }

    /**
     * Checks if the event name is allowed.
     *
     * @param {string} name - The event name to validate.
     * @returns {boolean} True if the event name is valid, false otherwise.
     * @description An event name is considered valid if it is not empty, not in the list of invalid events,
     *             and follows the naming convention (lowercase or camelCase).
     */
    static isValid(name) {
      if (!name || INVALID_EVENTS.indexOf(name) > -1) {
        return false;
      }

      // event name must be lowercase or camelCase, DT has an audit for this in place
      // so only log it as an info message
      if (!CAMELCASE_REG.test(name)) {
        logger.info('About to fire an event', name, 'that might be invalid. The name must be all lowercase or',
          'camelCased!');
      }

      return true;
    }
  }

  return EventAction;
});

